import {
  CreateButton,
  DateField,
  List,
  SaveButton,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Card, Col, Divider, Form, FormProps, Input, Row, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TabsProps } from "antd";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useCan } from "@refinedev/core";

import { ResourceEnum } from "accessControlProvider";
import { IShipco, IVesselTransfer } from "interfaces";
import React from "react";
import { useNoticeCounts } from "./ukho/api";
import { BackStillParkedTab } from "./ukho/BackStillParkedTab";
import { LeftNotParkedTab } from "./ukho/LeftNotParkedTab";
import { UnmatchedTab } from "./ukho/UnmatchedTab";


const TRANSFER_TYPE_LABELS: Record<string, string> = {
  to_old_vessels: "Deactivate",
  between_shipcos: "Transfer",
  from_old_vessels: "Reactivate",
};

const TRANSFER_TYPE_COLORS: Record<string, string> = {
  to_old_vessels: "volcano",
  between_shipcos: "geekblue",
  from_old_vessels: "cyan",
};

const SCREEN_SUBTITLE =
  "Park a vessel, move it between customers, or reactivate it.";

/** Said only to the roles that see the three UKHO notice tabs. */
const NOTICE_TABS_SUBTITLE =
  "The three new tabs come from UKHO's transfer notices.";

const TRANSFER_HISTORY_TAB_KEY = "transfer-history";

export const VesselTransferList: React.FC<IResourceComponentsProps> = () => {
  const {
    tableProps,
    tableQuery: { isLoading, refetch: refetchTransferHistory },
    searchFormProps,
    filters,
  } = useTable<IVesselTransfer>({
    resource: "vessel-transfer",
    meta: {
      join: [
        { field: "sourceShipco" },
        { field: "targetShipco" },
        { field: "items" },
      ],
    },
    onSearch: (params: any) => {
      const filters: CrudFilters = [];
      const { transferType, sourceShipco, targetShipco, performedBy } = params;

      filters.push({
        field: "transferType",
        operator: "eq",
        value: transferType ? transferType : undefined,
      });
      filters.push({
        field: "sourceShipco.registrationName",
        operator: "eq",
        value: sourceShipco ? sourceShipco : undefined,
      });
      filters.push({
        field: "targetShipco.registrationName",
        operator: "eq",
        value: targetShipco ? targetShipco : undefined,
      });
      filters.push({
        field: "performedBy",
        operator: "contains",
        value: performedBy ? performedBy : undefined,
      });

      return filters;
    },
    sorters: {
      initial: [{ field: "id", order: "desc" }],
    },
    pagination: {
      pageSize: 20,
      mode: "server",
    },
    filters: {
      defaultBehavior: "replace",
    },
  });

  // The three UKHO notice tabs are for the roles the notice API lets in.
  const { data: noticeAccess } = useCan({
    resource: ResourceEnum.UkhoTransferNotice,
    action: "list",
  });

  const columns: ColumnsType<IVesselTransfer> = [
    {
      title: "Transfer Type",
      dataIndex: "transferType",
      key: "transferType",
      width: 140,
      render: (value: string) => (
        <Tag color={TRANSFER_TYPE_COLORS[value] || "blue"}>{TRANSFER_TYPE_LABELS[value] || value}</Tag>
      ),
    },
    {
      title: "Source Shipco",
      key: "sourceShipco",
      width: 200,
      render: (_, record) => (
        <TextField
          value={record.sourceShipco?.registrationName || "N/A"}
        />
      ),
    },
    {
      title: "Target Shipco",
      key: "targetShipco",
      width: 200,
      render: (_, record) => (
        <TextField
          value={record.targetShipco?.registrationName || "N/A"}
        />
      ),
    },
    {
      title: "IMO Number",
      key: "imoNumber",
      width: 120,
      render: (_, record) => (
        <TextField value={record.items?.[0]?.imoNumber || "N/A"} />
      ),
    },
    {
      title: "Performed By",
      dataIndex: "performedBy",
      key: "performedBy",
      width: 150,
      render: (value) => <TextField value={value || "N/A"} />,
    },
    {
      title: "Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      width: 160,
      render: (value) =>
        value ? (
          <DateField value={value} format="DD-MMM-YYYY" />
        ) : (
          <TextField value="N/A" />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Space>
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  const transferHistory = (
    <Row gutter={[16, 16]}>
      <Col xl={24} lg={24} xs={24}>
        <Card type="inner" title={"Filter"}>
          <Filter formProps={searchFormProps} filters={filters || []} />
        </Card>
      </Col>
      <Col xl={24} xs={24}>
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          size="middle"
          scroll={{ x: 1200 }}
          sticky
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </Col>
    </Row>
  );

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Vessel Transfers",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <CreateButton>New Transfer</CreateButton>
          </Space>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Typography.Paragraph type="secondary">
        {noticeAccess?.can
          ? `${SCREEN_SUBTITLE} ${NOTICE_TABS_SUBTITLE}`
          : SCREEN_SUBTITLE}
      </Typography.Paragraph>
      {noticeAccess?.can ? (
        <TransferTabsWithNotices
          transferHistory={transferHistory}
          refetchTransferHistory={refetchTransferHistory}
        />
      ) : (
        <Tabs
          items={[
            {
              key: TRANSFER_HISTORY_TAB_KEY,
              label: "Transfer history",
              children: transferHistory,
            },
          ]}
        />
      )}
    </List>
  );
};

const NoticeTabLabel: React.FC<{ text: string; count: number | undefined }> = ({
  text,
  count,
}) => (
  <span>
    {text}
    {count !== undefined && (
      <Tag style={{ marginInlineStart: 8, marginInlineEnd: 0 }}>{count}</Tag>
    )}
  </span>
);

/**
 * "Transfer history" plus the three UKHO notice tabs (spec 11.2, 11.3), with
 * live counts on their labels. Only mounted for roles the notice API lets in,
 * so no other role sends the counts request. A hidden tab is unmounted, so a
 * tab shows fresh rows each time it opens; the counts refresh on every tab
 * change and after each park, reactivation or match.
 */
const TransferTabsWithNotices: React.FC<{
  transferHistory: React.ReactNode;
  refetchTransferHistory: () => void;
}> = ({ transferHistory, refetchTransferHistory }) => {
  const { data: counts, refetch: refetchCounts } = useNoticeCounts();

  const handleTabChange = (key: string) => {
    refetchCounts();
    if (key === TRANSFER_HISTORY_TAB_KEY) {
      refetchTransferHistory();
    }
  };

  const items: TabsProps["items"] = [
    {
      key: TRANSFER_HISTORY_TAB_KEY,
      label: "Transfer history",
      children: transferHistory,
    },
    {
      key: "left-not-parked",
      label: <NoticeTabLabel text="Left but not parked" count={counts?.leftNotParked} />,
      children: <LeftNotParkedTab refetchCounts={refetchCounts} />,
    },
    {
      key: "back-still-parked",
      label: <NoticeTabLabel text="Back but still parked" count={counts?.backStillParked} />,
      children: <BackStillParkedTab refetchCounts={refetchCounts} />,
    },
    {
      key: "unmatched-notices",
      label: <NoticeTabLabel text="Unmatched notices" count={counts?.unmatched} />,
      children: <UnmatchedTab refetchCounts={refetchCounts} />,
    },
  ];

  return <Tabs destroyOnHidden items={items} onChange={handleTabChange} />;
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  const { selectProps: sourceShipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: { mode: "server" },
  });

  const { selectProps: targetShipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: { mode: "server" },
  });

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        transferType: getDefaultFilter("transferType", filters),
        sourceShipco: getDefaultFilter("sourceShipco.registrationName", filters),
        targetShipco: getDefaultFilter("targetShipco.registrationName", filters),
        performedBy: getDefaultFilter("performedBy", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Transfer Type"} name="transferType">
            <Select placeholder="filter by type" allowClear>
              <Select.Option value="to_old_vessels">Deactivate</Select.Option>
              <Select.Option value="between_shipcos">Transfer</Select.Option>
              <Select.Option value="from_old_vessels">Reactivate</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Source Shipco"} name="sourceShipco">
            <Select
              placeholder="filter by source shipco"
              allowClear
              showSearch
              {...sourceShipcoSelectProps}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Target Shipco"} name="targetShipco">
            <Select
              placeholder="filter by target shipco"
              allowClear
              showSearch
              {...targetShipcoSelectProps}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Performed By"} name="performedBy">
            <Input placeholder="filter by user" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={4}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
